import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { Clip } from '../../types';
import { Type, Image as ImageIcon, Video, ChevronDown, Palette, Sparkles, Save, Check, Upload, RotateCcw, X } from 'lucide-react';
import { SaveMasterModal } from './SaveMasterModal';

interface SyntheContentEditorProps {
    clip: Clip;
    onUpdate: (clipId: string, updates: Partial<Clip>) => void;
    onSaveMaster?: (data: any) => void;
}

interface BackgroundVersion {
    url: string;
    name: string;
    type: 'image' | 'video';
    isOriginal?: boolean;
}

const AVAILABLE_FONTS = [
    // LVMH Fonts
    { name: 'LVMH Regular', family: '"LVMH Regular", sans-serif' },
    { name: 'LVMH Bold', family: '"LVMH Bold", sans-serif' },
    { name: 'LVMH Italic', family: '"LVMH Italic", sans-serif' },
    { name: 'LVMH Bold Italic', family: '"LVMH Bold Italic", sans-serif' },
    // Futura
    { name: 'Futura', family: 'Futura, sans-serif' },
    { name: 'Futura Bold', family: '"Futura Bold", Futura, sans-serif' },
    { name: 'Futura Italic', family: '"Futura Italic", Futura, sans-serif' },
    { name: 'Futura Bold Italic', family: '"Futura Bold Italic", Futura, sans-serif' },
];

export const SyntheContentEditor: React.FC<SyntheContentEditorProps> = ({ clip, onUpdate, onSaveMaster }) => {
    const [isSaved, setIsSaved] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [bgVersions, setBgVersions] = useState<BackgroundVersion[]>([]);
    const [showVersions, setShowVersions] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const nestedClips = clip.syntheData?.clips || [];
    const texts = nestedClips.filter(c => c.type === 'text');
    const visuals = nestedClips.filter(c => c.type === 'image' || c.type === 'video');
    const bgClip = nestedClips.find(c => c.role === 'background' || c.name === 'Background');
    const isBackgroundMedia = !!bgClip;
    const [activeTab, setActiveTab] = useState<'color' | 'media'>(isBackgroundMedia ? 'media' : 'color');

    // Sync active tab with external changes (e.g. undo/redo)
    useEffect(() => {
        setActiveTab(isBackgroundMedia ? 'media' : 'color');
    }, [isBackgroundMedia]);

    // Seed the version list with the current background on first load
    useEffect(() => {
        if (bgClip && bgVersions.length === 0) {
            setBgVersions([{
                url: bgClip.url || '',
                name: bgClip.name || 'Background',
                type: (bgClip.type as 'image' | 'video') || 'image',
                isOriginal: true,
            }]);
        }
    }, [bgClip?.id]);

    const handleSaveMaster = (data: { name: string; updateContent: boolean }) => {
        if (onSaveMaster) {
            onSaveMaster({ name: data.name, updateContent: data.updateContent });
            setIsSaved(true);
            setTimeout(() => setIsSaved(false), 2000);
        }
    };

    const handleTextChange = (childId: string, newText: string) => {
        const newNestedClips = nestedClips.map(c =>
            c.id === childId ? { ...c, text: newText } : c
        );
        onUpdate(clip.id, {
            syntheData: { ...clip.syntheData!, clips: newNestedClips }
        });
    };

    const handleTextStyleChange = (childId: string, styleUpdates: any) => {
        const newNestedClips = nestedClips.map(c => {
            if (c.id === childId) {
                return { ...c, style: { ...c.style, ...styleUpdates } };
            }
            return c;
        });
        onUpdate(clip.id, {
            syntheData: { ...clip.syntheData!, clips: newNestedClips }
        });
    };

    /** Replace or add a background clip with the given media */
    const setBackgroundMedia = useCallback((url: string, name: string, mediaType: 'image' | 'video') => {
        // Save current background as a version before replacing
        if (bgClip && bgClip.url && bgClip.url !== url) {
            setBgVersions(prev => {
                const alreadyExists = prev.some(v => v.url === bgClip.url);
                if (alreadyExists) return prev;
                return [...prev, {
                    url: bgClip.url!,
                    name: bgClip.name || 'Background',
                    type: (bgClip.type as 'image' | 'video') || 'image',
                    isOriginal: prev.length === 0,
                }];
            });
        }

        const newBgClip: any = {
            id: bgClip?.id || `panel-bg-${Date.now()}`,
            type: mediaType,
            name: name || 'Background',
            role: 'background',
            url,
            trackId: bgClip?.trackId || 'v1',
            start: 0,
            duration: clip.duration || 5,
            originalDuration: clip.duration || 5,
            trimStart: 0,
            volume: 1,
            opacity: 1,
            fadeInDuration: 0,
            fadeOutDuration: 0,
            transform: bgClip?.transform || { x: 0, y: 0, scale: 1, rotation: 0 },
        };

        const otherClips = nestedClips.filter(c => c.role !== 'background' && c.name !== 'Background');
        onUpdate(clip.id, {
            syntheData: { ...clip.syntheData!, clips: [newBgClip, ...otherClips] }
        });
        setActiveTab('media');
    }, [bgClip, nestedClips, clip, onUpdate]);

    /** Handle drop from library (application/json) or file system */
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        // Try library drag data first
        if ((e.dataTransfer as any).types.includes('application/json')) {
            try {
                const jsonData = (e.dataTransfer as any).getData('application/json');
                const data = JSON.parse(jsonData);
                if (data.url) {
                    const mediaType = data.type === 'video' ? 'video' : 'image';
                    setBackgroundMedia(data.url, data.name || 'Background', mediaType);
                    return;
                }
            } catch (err) {
                console.warn('[SyntheContentEditor] Failed to parse drag JSON:', err);
            }
        }

        // Fallback: file drop from OS
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            handleFileUpload(file);
        }
    }, [setBackgroundMedia]);

    /** Handle local file selection */
    const handleFileUpload = useCallback((file: File) => {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        if (!isImage && !isVideo) {
            alert('Veuillez sélectionner une image ou une vidéo.');
            return;
        }
        // Create a local blob URL — it will be uploaded to R2 on project save
        const blobUrl = URL.createObjectURL(file);
        const mediaType = isVideo ? 'video' : 'image';
        setBackgroundMedia(blobUrl, file.name, mediaType);
    }, [setBackgroundMedia]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    /** Revert to a previously saved background version */
    const revertToVersion = useCallback((version: BackgroundVersion) => {
        setBackgroundMedia(version.url, version.name, version.type);
        setShowVersions(false);
    }, [setBackgroundMedia]);

    if (nestedClips.length === 0) return null;

    return (
        <div className="bg-white text-gray-900">
            <div className="px-6 py-5 space-y-5">
                {/* Hidden file input for background media */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(file);
                        if (e.target) e.target.value = '';
                    }}
                />

                {/* Background Control for Panel */}
                {clip.type === 'panel' && (
                    <div className="space-y-3">
                        <label className="text-[11px] font-medium uppercase tracking-wider text-gray-500 flex items-center gap-2">
                            <Palette className="w-3.5 h-3.5" />
                            Arrière-plan
                        </label>

                        {/* Background Mode Toggle */}
                        <div className="flex bg-gray-100 p-1 rounded-lg">
                            <button
                                onClick={() => setActiveTab('color')}
                                className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-medium rounded-md transition-all ${activeTab === 'color'
                                    ? 'bg-white text-black shadow-sm'
                                    : 'text-gray-500 hover:text-gray-900'
                                    }`}
                            >
                                <div className="w-3 h-3 rounded-full bg-black border border-gray-300"></div>
                                Couleur
                            </button>
                            <button
                                onClick={() => setActiveTab('media')}
                                className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-medium rounded-md transition-all ${activeTab === 'media'
                                    ? 'bg-white text-black shadow-sm'
                                    : 'text-gray-500 hover:text-gray-900'
                                    }`}
                            >
                                <ImageIcon className="w-3.5 h-3.5" />
                                Média
                            </button>
                        </div>

                        {/* Controls based on active tab */}
                        {activeTab === 'color' ? (
                            <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                <input
                                    type="color"
                                    value={clip.syntheData?.backgroundColor || '#000000'}
                                    onChange={(e) => onUpdate(clip.id, {
                                        syntheData: { ...clip.syntheData!, backgroundColor: e.target.value }
                                    })}
                                    className="w-10 h-10 cursor-pointer border border-gray-300 bg-transparent rounded-md"
                                />
                                <div className="flex-1">
                                    <input
                                        type="text"
                                        value={clip.syntheData?.backgroundColor || '#000000'}
                                        onChange={(e) => onUpdate(clip.id, {
                                            syntheData: { ...clip.syntheData!, backgroundColor: e.target.value }
                                        })}
                                        className="w-full bg-transparent text-sm font-mono text-gray-700 border-b border-transparent focus:border-black outline-none py-1"
                                    />
                                    <p className="text-[10px] text-gray-400">Code couleur HEX</p>
                                </div>
                            </div>
                        ) : (
                            <div
                                className={`p-3 bg-gray-50 border rounded-lg space-y-2 transition-all ${isDragOver
                                    ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-200'
                                    : 'border-gray-200'
                                    }`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                {isBackgroundMedia ? (
                                    <>
                                        {/* Current background preview */}
                                        <div className="flex items-center gap-3">
                                            {/* Thumbnail preview */}
                                            {bgClip?.url && bgClip.type === 'image' ? (
                                                <div className="w-10 h-10 rounded overflow-hidden bg-gray-200 shrink-0">
                                                    <img src={bgClip.url} alt="" className="w-full h-full object-cover" />
                                                </div>
                                            ) : bgClip?.type === 'video' ? (
                                                <Video className="w-5 h-5 text-gray-500" />
                                            ) : (
                                                <ImageIcon className="w-5 h-5 text-gray-500" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-medium text-gray-900 truncate">
                                                    {bgClip?.name || 'Média sans nom'}
                                                </p>
                                                <p className="text-[10px] text-gray-400">Arrière-plan actif</p>
                                            </div>
                                            <button
                                                className="text-[10px] text-red-500 hover:text-red-700 font-medium px-2 py-1 hover:bg-red-50 rounded"
                                                onClick={() => {
                                                    if (bgClip) {
                                                        // Save to versions before removing
                                                        if (bgClip.url) {
                                                            setBgVersions(prev => {
                                                                const alreadyExists = prev.some(v => v.url === bgClip.url);
                                                                if (alreadyExists) return prev;
                                                                return [...prev, {
                                                                    url: bgClip.url!,
                                                                    name: bgClip.name || 'Background',
                                                                    type: (bgClip.type as 'image' | 'video') || 'image',
                                                                }];
                                                            });
                                                        }
                                                        const newClips = nestedClips.filter(c => c.id !== bgClip.id);
                                                        onUpdate(clip.id, {
                                                            syntheData: { ...clip.syntheData!, clips: newClips }
                                                        });
                                                    }
                                                }}
                                            >
                                                Supprimer
                                            </button>
                                        </div>

                                        {/* Action buttons */}
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="flex-1 py-2 border border-dashed border-gray-300 rounded text-xs text-gray-600 hover:border-gray-400 hover:bg-gray-100 transition-colors flex items-center justify-center gap-1.5"
                                            >
                                                <Upload className="w-3 h-3" />
                                                Remplacer
                                            </button>
                                            {bgVersions.length > 1 && (
                                                <button
                                                    onClick={() => setShowVersions(!showVersions)}
                                                    className="py-2 px-3 border border-dashed border-gray-300 rounded text-xs text-gray-600 hover:border-gray-400 hover:bg-gray-100 transition-colors flex items-center gap-1.5"
                                                >
                                                    <RotateCcw className="w-3 h-3" />
                                                    Versions ({bgVersions.length})
                                                </button>
                                            )}
                                        </div>

                                        {/* Version history */}
                                        {showVersions && bgVersions.length > 0 && (
                                            <div className="space-y-1.5 pt-1">
                                                <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Historique</p>
                                                {bgVersions.map((version, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={() => revertToVersion(version)}
                                                        className={`w-full flex items-center gap-2 p-2 rounded text-left transition-colors ${bgClip?.url === version.url
                                                            ? 'bg-blue-50 border border-blue-200'
                                                            : 'hover:bg-gray-100 border border-transparent'
                                                            }`}
                                                    >
                                                        {version.type === 'image' ? (
                                                            <div className="w-8 h-8 rounded overflow-hidden bg-gray-200 shrink-0">
                                                                <img src={version.url} alt="" className="w-full h-full object-cover" />
                                                            </div>
                                                        ) : (
                                                            <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center shrink-0">
                                                                <Video className="w-3.5 h-3.5 text-gray-500" />
                                                            </div>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[11px] font-medium text-gray-800 truncate">{version.name}</p>
                                                            <p className="text-[10px] text-gray-400">
                                                                {version.isOriginal ? '⭐ Original' : `Version ${idx + 1}`}
                                                            </p>
                                                        </div>
                                                        {bgClip?.url === version.url && (
                                                            <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div
                                        className={`flex flex-col items-center justify-center py-6 text-center border-2 border-dashed rounded-lg transition-all cursor-pointer ${isDragOver
                                            ? 'border-blue-400 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300 bg-white'
                                            }`}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        {isDragOver ? (
                                            <>
                                                <Upload className="w-8 h-8 text-blue-400 mb-2" />
                                                <p className="text-xs font-medium text-blue-600">Déposez ici</p>
                                            </>
                                        ) : (
                                            <>
                                                <ImageIcon className="w-8 h-8 text-gray-300 mb-2" />
                                                <p className="text-xs font-medium text-gray-900">Aucun média</p>
                                                <p className="text-[10px] text-gray-500 px-4 mt-1">
                                                    Glissez une image/vidéo depuis la bibliothèque ou cliquez pour importer.
                                                </p>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Revert to original — quick access when no versions panel open */}
                                {!isBackgroundMedia && bgVersions.length > 0 && (
                                    <div className="pt-1">
                                        <button
                                            onClick={() => {
                                                const original = bgVersions.find(v => v.isOriginal) || bgVersions[0];
                                                if (original) revertToVersion(original);
                                            }}
                                            className="w-full py-1.5 text-[11px] text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1.5 transition-colors"
                                        >
                                            <RotateCcw className="w-3 h-3" />
                                            Restaurer l'original
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Text Fields */}
                {texts.length > 0 && (
                    <div className="space-y-4">
                        <h4 className="text-[11px] font-medium uppercase tracking-wider text-gray-500 flex items-center gap-2">
                            <Type className="w-3.5 h-3.5" />
                            Textes ({texts.length})
                        </h4>

                        {texts.map((textClip, i) => (
                            <TextFieldCard
                                key={textClip.id}
                                textClip={textClip}
                                index={i}
                                onTextChange={handleTextChange}
                                onStyleChange={handleTextStyleChange}
                            />
                        ))}
                    </div>
                )}

                {/* Media List */}
                {visuals.length > 0 && (
                    <div className="space-y-3">
                        <h4 className="text-[11px] font-medium uppercase tracking-wider text-gray-500 flex items-center gap-2">
                            <Video className="w-3.5 h-3.5" />
                            Médias ({visuals.length})
                        </h4>
                        <div className="space-y-2">
                            {visuals.map((vis) => (
                                <div
                                    key={vis.id}
                                    className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
                                >
                                    <div className={`
                                        w-8 h-8 flex items-center justify-center
                                        ${vis.type === 'video' ? 'bg-gray-200 text-gray-600' : 'bg-gray-200 text-gray-600'}
                                    `}>
                                        {vis.type === 'video' ? <Video className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
                                    </div>
                                    <span className="text-sm text-gray-700 truncate flex-1">{vis.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Save Master Button */}
                {onSaveMaster && (
                    <div className="pt-4 border-t border-gray-200">
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className={`w-full py-3 px-4 font-medium text-sm transition-all flex items-center justify-center gap-2 ${isSaved ? 'bg-black text-white' : 'bg-black text-white hover:bg-gray-800'}`}
                        >
                            {isSaved ? (
                                <><Check className="w-4 h-4" /> Sauvegardé !</>
                            ) : (
                                <><Save className="w-4 h-4" /> Sauvegarder Master</>
                            )}
                        </button>
                    </div>
                )}
            </div>

            {/* Save Master Modal */}
            <SaveMasterModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveMaster}
                defaultName={clip.name}
                clipType={clip.type as 'synthe' | 'panel'}
            />
        </div>
    );
};

// Modern Text Field Card
interface TextFieldCardProps {
    textClip: any;
    index: number;
    onTextChange: (id: string, text: string) => void;
    onStyleChange: (id: string, style: any) => void;
}

const TextFieldCard: React.FC<TextFieldCardProps> = ({ textClip, index, onTextChange, onStyleChange }) => {
    const [localFont, setLocalFont] = useState(textClip.style?.fontFamily || 'Futura, sans-serif');
    const [localColor, setLocalColor] = useState(textClip.style?.color || '#FFFFFF');
    const [localFontSize, setLocalFontSize] = useState(textClip.style?.fontSize || 48);
    const [isExpanded, setIsExpanded] = useState(true);

    useEffect(() => {
        setLocalFont(textClip.style?.fontFamily || 'Futura, sans-serif');
        setLocalColor(textClip.style?.color || '#FFFFFF');
        setLocalFontSize(textClip.style?.fontSize || 48);
    }, [textClip.style?.fontFamily, textClip.style?.color, textClip.style?.fontSize]);

    return (
        <div className="bg-gray-50 border border-gray-200 overflow-hidden">
            {/* Card Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-100 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 flex items-center justify-center">
                        <Type className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="text-left">
                        <span className="text-xs font-medium text-gray-900">Texte {index + 1}</span>
                        <p className="text-[10px] text-gray-500 truncate max-w-[150px]">
                            {textClip.text || 'Vide...'}
                        </p>
                    </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>

            {isExpanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-gray-200 pt-4">
                    {/* Style Controls - Row 1: Font + Size */}
                    <div className="grid grid-cols-2 gap-3">
                        {/* Font Selector */}
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500">Police</label>
                            <select
                                value={localFont}
                                onChange={(e) => {
                                    setLocalFont(e.target.value);
                                    onStyleChange(textClip.id, { fontFamily: e.target.value });
                                }}
                                className="w-full text-xs bg-white border border-gray-300 px-3 py-2.5 text-gray-900 focus:outline-none focus:border-black appearance-none cursor-pointer"
                                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='gray' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '16px' }}
                            >
                                {AVAILABLE_FONTS.map(f => (
                                    <option key={f.name} value={f.family}>{f.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Font Size */}
                        <div className="space-y-1.5">
                            <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500">Taille</label>
                            <div className="flex items-center gap-2 bg-white border border-gray-300 px-3 py-2">
                                <input
                                    type="number"
                                    min="8"
                                    max="200"
                                    value={localFontSize}
                                    onChange={(e) => {
                                        const size = Number(e.target.value);
                                        setLocalFontSize(size);
                                        onStyleChange(textClip.id, { fontSize: size });
                                    }}
                                    className="flex-1 text-xs bg-transparent text-gray-900 border-0 outline-none font-mono"
                                />
                                <span className="text-xs text-gray-400">px</span>
                            </div>
                        </div>
                    </div>

                    {/* Style Controls - Row 2: Color */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500">Couleur</label>
                        <div className="flex items-center gap-2 bg-white border border-gray-300 px-2 py-1.5">
                            <input
                                type="color"
                                value={localColor}
                                onChange={(e) => {
                                    setLocalColor(e.target.value);
                                    onStyleChange(textClip.id, { color: e.target.value });
                                }}
                                className="w-8 h-8 cursor-pointer border-0 bg-transparent"
                            />
                            <input
                                type="text"
                                value={localColor}
                                onChange={(e) => {
                                    setLocalColor(e.target.value);
                                    onStyleChange(textClip.id, { color: e.target.value });
                                }}
                                className="flex-1 text-[11px] font-mono bg-transparent text-gray-700 border-0 outline-none uppercase"
                            />
                        </div>
                    </div>

                    {/* Text Input */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500">Contenu</label>
                        <textarea
                            value={textClip.text || ''}
                            onChange={(e) => onTextChange(textClip.id, e.target.value)}
                            placeholder="Saisir le texte..."
                            className="w-full text-sm bg-white border border-gray-300 p-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-black resize-none min-h-[80px] transition-all"
                            style={{ fontFamily: localFont }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SyntheContentEditor;
